import React, { Fragment, useRef, useState } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import InfoIcon from '../../../../images/uploadDocs/icon-info.svg';

import { MAX_FILE_SIZE_LIMIT, ACCEPTED_MEDIA_TYPES, ACCEPTED_MEDIA_TYPES_MESSAGE } from '../constants';

import styles from './UploadSection.module.scss';

const UploadSection = (({
  required,
  groupedFiles,
  fileError,
  safariFileError,
  sizeError,
  type,
  title,
  removeFile,
  fileDrop,
  handleDrop,
  dragEnter,
  dragLeave,
  icon
}) => {
  const inputRef = useRef();
  const [isHovered, setHovering] = useState(false);
  const [dropZoneIsHovered, setDropZoneHovering] = useState(false);

  const filesSelected = (e) => {
    handleDrop(e.target.files, type);
  };

  const fileNameDisplay = (filename) => {
    if (filename.length > 35) {
      const start = filename.substr(0, 25);
      const index = filename.lastIndexOf('.');
      const end = filename.substr(index - 3);
      return `${start}...${end}`;
    }

    return filename;
  };

  const hasError = (fileError && fileError.type === type) ||
    (sizeError && sizeError.includes(type)) ||
    (safariFileError && safariFileError.includes(type));

  const onDragEnter = (e) => {
    if (!dropZoneIsHovered && !hasError) {
      setDropZoneHovering(true);
    }

    if (hasError) {
      setDropZoneHovering(false);
    }

    dragEnter(e, { type });
  };

  const onDragLeave = (e) => {
    setDropZoneHovering(false);

    dragLeave(e, { type });
  };

  const onFileDrop = (e) => {
    e.preventDefault();

    setDropZoneHovering(false);

    fileDrop(e, { type });
  };

  const handleMouseOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHovering(true);
  };

  const handleMouseOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHovering(false);
  };

  const sectionClasses = classNames({
    [styles.uploadSection]: true,
    [styles['uploadSection--hover']]: dropZoneIsHovered && !hasError,
    [styles['uploadSection--error']]: hasError,
  });

  const dropzoneClasses = classNames({
    [styles.dropZone]: true,
    [styles['dropZone--error']]: hasError,
  });


  let errMessage = null;
  if (fileError && fileError.type === type) {
    errMessage =
    (<div className={styles.errorMessage}>{fileError && fileError.message}</div>);
  } else if ((sizeError && sizeError.includes(type)) ||
    (safariFileError && safariFileError.includes(type))) {
    errMessage = (<div className={styles.errorMessage}>
      {sizeError && <span>{`${MAX_FILE_SIZE_LIMIT}MB max file size`}</span>}
      {safariFileError && <span>{ACCEPTED_MEDIA_TYPES_MESSAGE}</span>}
    </div>);
  }

  let contentHeader;

  let content = (
    <Fragment>
      <div
        role="button"
        className={dropzoneClasses}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onFileDrop}
        data-hook={type}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            inputRef.current.click();
          }
        }}
      >
        <img
          src={`/assets/images/uploadDocs/${icon}`}
          alt={type}
          className={styles.icon}
        />
        <span className={styles.textBold}>{title}</span>
        <span>Drag & Drop Files to upload</span>
        <span className={styles.selectedFile}>
          {groupedFiles && groupedFiles.size > 0 && groupedFiles.get(type) &&
          groupedFiles.get(type).map(file => {
            const errorClass = classNames({
              [styles.fileNameError]: file.sizeError || file.fileTypeError,
            });
            return (<div
              role="button"
              tabIndex="0"
              onClick={() => removeFile(file, { type })}
              key={`${file.uploadTime}_${file.name}`}
              className={styles.fileName}
            >
              <span className={errorClass}>{fileNameDisplay(file.name)}</span>
            </div>);
          })}
        </span>
        {errMessage}
        <input
          key={Date.now()}
          name={type}
          disabled={false}
          type="file"
          tabIndex="-1"
          style={{ overflow: 'hidden' }}
          onChange={filesSelected}
          ref={inputRef}
          multiple
          accept={ACCEPTED_MEDIA_TYPES.join()}
        />
      </div>
      {!fileError && !sizeError && !safariFileError && (
        <span className={styles.link}>
          Browse Files
        </span>
      )}
    </Fragment>
  );

  if (isHovered) {
    if (type === 'id-docs') {
      contentHeader = (
        <div className={styles.infoHeader}>
          Proof of Identity
        </div>
      );

      content = (
        <div className={styles.sectionInfo}>
          <p>Please provide one of the following accepted documents as a Proof of Identity: </p>
          <ul className={styles.list}>
            <li>Passport</li>
            <li>Drivers License (front AND back)</li>
            <li>National ID Card (front AND back)</li>
          </ul>
        </div>
      );
    }
    if (type === 'address-docs') {
      contentHeader = (
        <div className={styles.infoHeader}>
          Proof of Address
        </div>
      );

      content = (
        <div className={styles.sectionInfo}>
          <p>{'Please provide one of the following accepted documents dated within the last 3 months as a Proof of Address: '}</p>
          <ul className={styles.list}>
            <li>{'Utility Bill such as Water, Gas or Electricity. (Mobile phone bills are NOT accepted)'}</li>
            <li>Bank Statement(s) (Transaction details may be blacked out)</li>
            <li>Government or Council issued letter</li>
          </ul>
          <div className={styles.infoNotes}>
            <p>{"Please note, we are unable to accept a Driver's License as sole proof of Address."}</p>
          </div>
        </div>
      );
    }

    if (type === 'other-docs') {
      contentHeader = (
        <div className={styles.infoHeader}>
          Other Documents
        </div>
      );

      content = (
        <div className={styles.sectionInfo}>
          <p><u>Please only provide the following if instructed by our team.</u></p>
          <p>{'As part of our Enhanced Due Diligence, we may request documents to identify your source of funds used on site.'}</p>
          <p>We may require proof of one or more of:</p>
          <ul className={styles.list}>
            <li>Salary</li>
            <li>Gift/Inheritance</li>
            <li>Company Profits</li>
            <li>Other documents that show source of funds</li>
          </ul>
          <p>If in doubt, contact our Customer Service team.</p>
        </div>
      );
    }
  }

  return (
    <div className={sectionClasses}>
      <div
        className={styles.info}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseOut}
      >
        <InfoIcon />
        {contentHeader}
      </div>
      {['id-docs', 'address-docs'].includes(type) && required && (
        <span className={styles.requiredLabel}>Required for all new accounts</span>
      )}
      {content}
    </div>
  );
});

UploadSection.propTypes = {
  groupedFiles: PropTypes.instanceOf(Map),
  fileError: PropTypes.object,
  safariFileError: PropTypes.array,
  sizeError: PropTypes.array,
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  removeFile: PropTypes.func.isRequired,
  fileDrop: PropTypes.func.isRequired,
  handleDrop: PropTypes.func.isRequired,
  dragEnter: PropTypes.func.isRequired,
  dragLeave: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  required: PropTypes.bool,
};

export default UploadSection;

